import { useEffect, useState, useCallback } from "react";
import { Tag, FloppyDisk, ArrowClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { updateUtm, utmPreview, resetUtm } from "../lib/api";

const FIELDS = [
  {
    key: "source",
    label: "utm_source",
    placeholder: "(default: platform key)",
    hint: "e.g. facebook_ads, google, newsletter",
  },
  {
    key: "medium",
    label: "utm_medium",
    placeholder: "(default: cpc)",
    hint: "cpc / email / social / referral",
  },
  {
    key: "campaign",
    label: "utm_campaign",
    placeholder: "(default: slugified campaign name)",
    hint: "supports {platform} {campaign_slug}",
  },
  {
    key: "content_template",
    label: "utm_content",
    placeholder: "(default: {variant})",
    hint: "supports {platform} {variant} {variant_letter}",
  },
  {
    key: "term",
    label: "utm_term (optional)",
    placeholder: "keyword or targeting note",
    hint: "supports {platform} {variant}",
  },
];

export default function UtmBuilderDialog({ open, onOpenChange, adSetId, onSaved }) {
  const [cfg, setCfg] = useState({
    source: "",
    medium: "",
    campaign: "",
    content_template: "",
    term: "",
  });
  const [previews, setPreviews] = useState([]);
  const [saving, setSaving] = useState(false);

  const loadPreview = useCallback(async () => {
    if (!adSetId) return;
    try {
      const data = await utmPreview(adSetId);
      setPreviews(data.previews || []);
      const c = data.utm_config || {};
      setCfg({
        source: c.source || "",
        medium: c.medium || "",
        campaign: c.campaign || "",
        content_template: c.content_template || "",
        term: c.term || "",
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load preview");
    }
  }, [adSetId]);

  useEffect(() => {
    if (open && adSetId) loadPreview();
  }, [open, adSetId, loadPreview]);

  const save = async () => {
    if (!adSetId) return;
    setSaving(true);
    try {
      await updateUtm(adSetId, cfg);
      await loadPreview();
      toast.success("UTM tags saved · new tracker URLs live");
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!adSetId) return;
    setSaving(true);
    try {
      await resetUtm(adSetId);
      await loadPreview();
      toast.success("Reset to platform defaults");
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0a0a0a] border border-white/10 rounded-none max-w-3xl max-h-[85vh] overflow-y-auto"
        data-testid="utm-builder-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Tag size={22} weight="fill" className="text-[#ccff00]" />
            UTM builder
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-sm">
            Customize the tags appended to every tracker URL for this ad set.
            Leave a field blank to fall back to the platform default. Templates
            support <code className="text-[#ccff00]">{"{platform}"}</code>,{" "}
            <code className="text-[#ccff00]">{"{variant}"}</code>,{" "}
            <code className="text-[#ccff00]">{"{variant_letter}"}</code>,{" "}
            <code className="text-[#ccff00]">{"{campaign_slug}"}</code>.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-3">
          {FIELDS.map((f) => (
            <div key={f.key}>
              <div className="label-mono mb-1.5">{f.label}</div>
              <Input
                data-testid={`utm-${f.key}-input`}
                value={cfg[f.key]}
                onChange={(e) => setCfg({ ...cfg, [f.key]: e.target.value })}
                placeholder={f.placeholder}
                className="rounded-none bg-[#121212] border-white/10 h-10 font-mono text-xs"
              />
              <div className="text-[10px] text-zinc-600 mt-1 font-mono">
                {f.hint}
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-2 pt-2 border-t border-white/5">
          <button
            data-testid="utm-save-btn"
            onClick={save}
            disabled={saving}
            className="bg-[#ccff00] text-black hover:bg-[#b3e600] disabled:opacity-40 font-semibold py-2.5 px-4 flex items-center gap-2 transition-colors"
          >
            <FloppyDisk size={13} weight="fill" />
            {saving ? "Saving…" : "Save UTM"}
          </button>
          <button
            data-testid="utm-reset-btn"
            onClick={reset}
            disabled={saving}
            className="border border-white/10 hover:border-white/30 text-zinc-400 hover:text-white py-2.5 px-4 flex items-center gap-2 font-mono text-xs uppercase tracking-wider transition-colors"
          >
            <ArrowClockwise size={12} />
            reset
          </button>
        </div>

        {previews.length > 0 && (
          <div className="mt-4 pt-4 border-t border-white/5">
            <div className="label-mono mb-3">live preview · sample tracker URLs</div>
            <div className="space-y-2">
              {previews.map((p) => (
                <div
                  key={p.platform}
                  className="bg-[#050505] border border-white/5 p-2.5"
                  data-testid={`utm-preview-${p.platform}`}
                >
                  <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-500 mb-1">
                    {p.platform}
                  </div>
                  <div className="font-mono text-[10.5px] text-[#ccff00] break-all">
                    ?{p.example_query}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
